import React, { createContext, useContext, useState, useRef } from 'react';
import { useToast } from '@chakra-ui/react';

const AudioContext = createContext(null);

export const useAudioContext = () => useContext(AudioContext);

export function AudioProvider({ children }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [currentTextId, setCurrentTextId] = useState(null); // Para saber qué texto se está leyendo
  const [currentTitle, setCurrentTitle] = useState('');
  
  const utteranceRef = useRef(null);
  const toast = useToast();

  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const playText = (id, title, text) => {
    if (!window.speechSynthesis) {
      toast({
        title: 'Error',
        description: 'Tu navegador no soporta lectura de texto a voz.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const cleanText = stripHtml(title + ". " + text);
    if (!cleanText.trim()) return;

    // Si ya está leyendo y hacemos click en otro
    if (isPlaying && currentTextId !== id) {
      window.speechSynthesis.cancel();
    }

    const utterance = new SpeechSynthesisUtterance(cleanText);
    utterance.lang = 'es-ES';
    
    utterance.onstart = () => {
      setIsPlaying(true);
      setIsPaused(false);
      setCurrentTextId(id);
      setCurrentTitle(title);
      
      // Update Media Session
      if ('mediaSession' in navigator) {
        navigator.mediaSession.metadata = new window.MediaMetadata({
          title: title,
          artist: 'Sala Evangélica',
          album: 'Devocionales y Estudios',
          artwork: [
            { src: '/logo192.png', sizes: '192x192', type: 'image/png' },
            { src: '/logo512.png', sizes: '512x512', type: 'image/png' }
          ]
        });
        navigator.mediaSession.playbackState = 'playing';
      }
    };
    
    utterance.onend = () => {
      setIsPlaying(false);
      setIsPaused(false);
      setCurrentTextId(null);
      setCurrentTitle('');
      if ('mediaSession' in navigator) navigator.mediaSession.playbackState = 'none';
    };
    
    utterance.onerror = (e) => {
      if (e.error !== 'canceled') {
        console.error('Speech synthesis error', e);
        setIsPlaying(false);
        setIsPaused(false);
        setCurrentTextId(null);
        if ('mediaSession' in navigator) navigator.mediaSession.playbackState = 'none';
      }
    };

    utteranceRef.current = utterance;
    window.speechSynthesis.speak(utterance);
  };

  const togglePlayPause = () => {
    if (!window.speechSynthesis) return;

    if (isPlaying) {
      if (isPaused) {
        window.speechSynthesis.resume();
        setIsPaused(false);
        if ('mediaSession' in navigator) navigator.mediaSession.playbackState = 'playing';
      } else {
        window.speechSynthesis.pause();
        setIsPaused(true);
        if ('mediaSession' in navigator) navigator.mediaSession.playbackState = 'paused';
      }
    }
  };

  const stopAudio = () => {
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
      setIsPlaying(false);
      setIsPaused(false);
      setCurrentTextId(null);
      setCurrentTitle('');
      if ('mediaSession' in navigator) navigator.mediaSession.playbackState = 'none';
    }
  };

  // Bind Media Session actions
  React.useEffect(() => {
    if ('mediaSession' in navigator) {
      navigator.mediaSession.setActionHandler('play', () => {
        togglePlayPause();
      });
      navigator.mediaSession.setActionHandler('pause', () => {
        togglePlayPause();
      });
      navigator.mediaSession.setActionHandler('stop', () => {
        stopAudio();
      });
    }
  }, [isPlaying, isPaused]);

  return (
    <AudioContext.Provider value={{
      isPlaying,
      isPaused,
      currentTextId,
      currentTitle,
      playText,
      togglePlayPause,
      stopAudio
    }}>
      {children}
    </AudioContext.Provider>
  );
}
